import React, { useEffect, useRef, useState } from 'react';
import userService from '../../services/userService';

/**
 * Reusable ProfileImage component that follows the same pattern as the header
 * @param {string} userId - User ID to fetch profile image for
 * @param {string} userName - User name for initials fallback
 * @param {string} imagePath - Direct S3 path (optional, will be fetched if not provided)
 * @param {string} className - Additional CSS classes
 * @param {string} size - Size variant: 'sm' (32px), 'md' (48px), 'lg' (64px), 'xl' (80px)
 * @param {boolean} showLoading - Whether to show loading state
 * @param {boolean} lazy - Whether to lazy load the image (default: true)
 * @param {function} onError - Error callback function
 * @param {function} onLoad - Load callback function
 */
const ProfileImage = ({ 
  userId, 
  userName, 
  imagePath = null, 
  className = '', 
  size = 'md',
  showLoading = true,
  lazy = true,
  onError = null,
  onLoad = null
}) => {
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isLoadingProfileImage, setIsLoadingProfileImage] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!lazy); // If not lazy, start as in view
  const containerRef = useRef(null);

  // Size mapping
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12', 
    lg: 'w-16 h-16',
    xl: 'w-20 h-20'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl', 
    xl: 'text-2xl'
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [lazy, isInView]);

  // Fetch profile image when component is in view or when props change
  useEffect(() => {
    // Only fetch if in view (or lazy loading is disabled)
    if (!isInView) {
      return;
    }

    const fetchProfileImage = async () => {
      if (!userId && !imagePath) {
        setProfileImageUrl(null);
        return;
      }

      setIsLoadingProfileImage(showLoading);
      setHasError(false);

      try {
        let imageUrl = null;

        // If imagePath is provided, check if it's already a URL or needs conversion
        if (imagePath) {
          if (imagePath.startsWith('http')) {
            // It's already a URL, use it directly
            console.log('🖼️ ProfileImage - Got direct URL:', imagePath);
            imageUrl = imagePath;
          } else {
            // It's an S3 path, convert to presigned URL
            console.log('🖼️ ProfileImage - Got S3 path, fetching presigned URL:', imagePath);
            imageUrl = await userService.getS3PresignedUrl(imagePath);
          }
        } else if (userId) {
          console.log('🖼️ ProfileImage - Fetching profile image for user:', userId);
          imageUrl = await userService.getProfileImageUrl(userId);
        }

        console.log('🖼️ ProfileImage - Image URL:', imageUrl);
        setProfileImageUrl(imageUrl);
      } catch (error) {
        console.warn('🖼️ ProfileImage - Error fetching profile image (will show initials):', error);
        // Don't set hasError to true for API errors - just show initials
        setProfileImageUrl(null);
        setHasError(false); // Changed from true to false
        if (onError) onError(error);
      } finally {
        setIsLoadingProfileImage(false);
      }
    };

    fetchProfileImage();
  }, [userId, imagePath, showLoading, onError, isInView]);

  const handleImageError = (e) => {
    console.log('🖼️ ProfileImage - Image failed to load:', profileImageUrl, e);
    setProfileImageUrl(null);
    setHasError(true);
    if (onError) onError(e);
  };

  const handleImageLoad = (e) => {
    console.log('🖼️ ProfileImage - Image loaded successfully:', profileImageUrl);
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  const getInitials = () => {
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    return '?';
  };

  // If className contains size overrides, use them instead of size prop
  const hasCustomSize = className && (className.includes('w-') || className.includes('h-'));
  const sizeClass = hasCustomSize ? '' : sizeClasses[size];
  
  // Extract rounded classes from className, default to rounded-lg
  // Matches: rounded-lg, rounded-3xl, rounded-full, rounded-[3px], etc.
  const roundedClassMatch = className.match(/rounded-[\w\[\]]+/);
  const roundedClass = roundedClassMatch ? roundedClassMatch[0] : 'rounded-lg';
  
  // Remove rounded classes and outline from className for inner elements (they're applied separately)
  const classNameForInner = className
    .replace(/rounded-[\w\[\]]+/g, '')
    .replace(/outline[^\s]*/g, '')
    .trim();
  
  const baseClasses = `${sizeClass} relative ${roundedClass} flex-shrink-0 overflow-hidden bg-gray-200 ${className}`;

  return (
    <div ref={containerRef} className={baseClasses}>
      {isLoadingProfileImage ? (
        <div className={`${hasCustomSize ? classNameForInner || sizeClasses[size] : sizeClasses[size]} left-0 top-0 absolute ${roundedClass} bg-gray-200 flex items-center justify-center`}>
          <div className={`${hasCustomSize ? classNameForInner || sizeClasses[size] : sizeClasses[size]} border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin`}></div>
        </div>
      ) : profileImageUrl && !hasError ? (
        <img
          className={`${hasCustomSize ? classNameForInner || sizeClasses[size] : sizeClasses[size]} left-0 top-0 absolute ${roundedClass} object-cover`}
          src={profileImageUrl}
          alt={userName || 'Profile'}
          loading={lazy ? 'lazy' : 'eager'}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      ) : userName ? (
        <div 
          className={`${hasCustomSize ? classNameForInner || sizeClasses[size] : sizeClasses[size]} left-0 top-0 absolute ${roundedClass} bg-blue-600 flex items-center justify-center text-white font-bold ${hasCustomSize ? 'text-lg' : textSizeClasses[size]}`}
        >
          {getInitials()}
        </div>
      ) : (
        <img
          className={`${hasCustomSize ? classNameForInner || sizeClasses[size] : sizeClasses[size]} left-0 top-0 absolute ${roundedClass}`}
          src={`https://placehold.co/${hasCustomSize ? '64' : size === 'sm' ? '32' : size === 'md' ? '48' : size === 'lg' ? '64' : '80'}x${hasCustomSize ? '64' : size === 'sm' ? '32' : size === 'md' ? '48' : size === 'lg' ? '64' : '80'}/000000/FFFFFF?text=U`}
          alt="Profile"
        />
      )}
    </div>
  );
};

export default ProfileImage;
